"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";
import { PublicKey } from "@solana/web3.js";

import { isKnownWalletId } from "@/lib/wallet/solana/quirks";

import type { WalletId } from "@/lib/wallet/solana/types";

const KEY = "mudwater:glyph-wallet-return";
const EVENT = "mudwater:glyph-wallet-return-changed";
export const GLYPH_WALLET_RETURN_TTL = 5 * 60_000;
export interface GlyphWalletReturn {
  walletId: WalletId;
  address: string;
  ticket: string;
  expiresAt: number;
}
function isReturnWalletId(value: unknown): value is WalletId {
  return (
    typeof value === "string" &&
    (isKnownWalletId(value) ||
      /^other:[^\u0000-\u001f\u007f]{1,80}$/.test(value))
  );
}
function parse(raw: string | null): GlyphWalletReturn | null {
  if (!raw || raw.length > 512) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object") return null;
    if (
      Object.keys(value).sort().join() !==
        "address,expiresAt,ticket,walletId" ||
      !("walletId" in value) ||
      !("address" in value) ||
      !("ticket" in value) ||
      !("expiresAt" in value)
    )
      return null;
    const { walletId, address, ticket, expiresAt } = value;
    if (
      !isReturnWalletId(walletId) ||
      typeof address !== "string" ||
      new PublicKey(address).toBase58() !== address ||
      typeof ticket !== "string" ||
      !/^[a-f0-9-]{36}$/.test(ticket) ||
      typeof expiresAt !== "number" ||
      !Number.isSafeInteger(expiresAt) ||
      expiresAt <= Date.now() ||
      expiresAt > Date.now() + GLYPH_WALLET_RETURN_TTL
    )
      return null;
    return { walletId, address, ticket, expiresAt };
  } catch {
    return null;
  }
}
function snapshot(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (parse(raw)) return raw;
    sessionStorage.removeItem(KEY);
  } catch {
    /* Storage denial means no return association. */
  }
  return null;
}
/** Identity association hint only. Never a connected wallet, proof, signer or recipient. */
export function readGlyphWalletReturn(): GlyphWalletReturn | null {
  return parse(snapshot());
}
/** Capture a return attempt once; a replacement ticket retires it immediately. */
export function beginGlyphWalletReturn(walletId: WalletId, address?: string) {
  if (!address) return { isCurrent: () => true, complete: () => undefined };
  const marker = readGlyphWalletReturn();
  if (marker?.walletId !== walletId || marker.address !== address)
    throw new Error("Reconnect expired. Choose your Solana wallet again.");
  return {
    isCurrent: () => readGlyphWalletReturn()?.ticket === marker.ticket,
    complete: () => clearGlyphWalletReturn(marker.ticket),
  };
}
export function clearGlyphWalletReturn(ticket?: string): void {
  if (ticket) {
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? "null");
      if (
        !value ||
        typeof value !== "object" ||
        !("ticket" in value) ||
        value.ticket !== ticket
      )
        return;
    } catch {
      return;
    }
  }
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* Optional hint. */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
}
/** Call only for the external wallet actually connected when the user asks for Glyph login. */
export function rememberGlyphWalletReturn(
  walletId: WalletId,
  address: string
): GlyphWalletReturn | null {
  clearGlyphWalletReturn();
  try {
    const item = {
      walletId,
      address,
      ticket: crypto.randomUUID(),
      expiresAt: Date.now() + GLYPH_WALLET_RETURN_TTL,
    };
    if (!parse(JSON.stringify(item))) return null;
    sessionStorage.setItem(KEY, JSON.stringify(item));
    window.dispatchEvent(new Event(EVENT));
    return item;
  } catch {
    return null;
  }
}
function subscribe(callback: () => void) {
  window.addEventListener(EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}
export function useGlyphWalletReturn(): GlyphWalletReturn | null {
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const marker = useMemo(() => parse(raw), [raw]);
  useEffect(() => {
    if (!marker) return;
    const timeout = setTimeout(
      () => clearGlyphWalletReturn(marker.ticket),
      Math.max(0, marker.expiresAt - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [marker]);
  return marker;
}
